import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from "../components/ui/button";

// Função para importar todas as imagens da galeria de forma dinâmica
const importAll = (r) => {
  return r.keys().map(r);
};

// Importa todas as imagens da pasta galeria
let images = [];
try {
  // O require.context é dinâmico: ele lê a pasta em tempo de build
  images = importAll(require.context('../assets/images/galeria', false, /\.(png|jpe?g|svg)$/));
} catch (e) {
  console.error("Erro ao carregar imagens da galeria:", e);
}

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openImage = (index) => {
    setSelectedImage(images[index]);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeImage = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    const nextIdx = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIdx]);
    setCurrentIndex(nextIdx);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    const prevIdx = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIdx]);
    setCurrentIndex(prevIdx);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Galeria de Projetos
          </h1>
          <p className="text-xl text-gray-600">
            Confira nossos trabalhos realizados. Esta galeria é atualizada automaticamente 
            sempre que novas fotos são adicionadas à pasta do projeto.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          {images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {images.map((image, index) => (
                <div
                  key={index}
                  onClick={() => openImage(index)}
                  className="group relative aspect-square overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer"
                >
                  <img
                    src={image}
                    alt={`Projeto Rei do Teto ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs md:text-sm font-medium px-4 py-2 border border-white rounded-full">
                      Ver Foto
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                Nenhuma imagem encontrada na pasta da galeria.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 md:p-10"
          onClick={closeImage}
        >
          <button 
            onClick={closeImage}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-[110]"
          >
            <X size={40} />
          </button>

          <button 
            onClick={prevImage}
            className="absolute left-4 md:left-10 text-white/70 hover:text-white transition-colors p-2 z-[110]"
          >
            <ChevronLeft size={48} />
          </button>

          <img 
            src={selectedImage} 
            alt="Trabalho ampliado"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in duration-300"
          />

          <button 
            onClick={nextImage}
            className="absolute right-4 md:right-10 text-white/70 hover:text-white transition-colors p-2 z-[110]"
          >
            <ChevronRight size={48} />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 font-medium bg-white/10 px-4 py-1 rounded-full backdrop-blur-sm z-[110]">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Gostou do Que Viu?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Solicite um orçamento sem compromisso e transforme seu ambiente com quem entende do assunto.
          </p>
          <Button 
            onClick={() => window.open(`https://wa.me/5527996369622`, '_blank')}
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-lg px-10 py-6"
          >
            Solicitar Orçamento no WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};
